import { Fragment, ReactNode, useState } from "react";
import { FieldValues, useController } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

import CreateBidButton from "components/bids/create_bid_button/CreateBidButton";
import { getLeadEntitiesForBid } from "components/leads/leads_table/utils";
import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "components/ui/combobox";
import type { ComboboxData, Option } from "components/ui/combobox/types";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import { useDebouncedValue } from "lib/debounce";
import type { SearchBidsRequest } from "lib/generated/app-service-gql/graphql";
import { useBidsSimple } from "lib/hooks/api/bid/bids/useBidsSimple";
import { LeadDetails } from "lib/hooks/api/leads/useLead";
import { routes } from "lib/routes";
import { BidSortBy, BidSortOrder } from "lib/types/graphQLEnums";
import { ComboboxProps } from "../../combobox/ComboBox";
import { EntityOption } from "./ComboboxOption";
import { EmptyComboboxTrigger } from "./EmptyComboboxTrigger";
import { SelectedEntityCard } from "./SelectedEntityCard";

export type RelatedBidComboboxProps<TFieldValues extends FieldValues = FieldValues> = Required<
  Pick<ComboboxProps<TFieldValues>, "control" | "name">
> & {
  defaultBidRequest?: SearchBidsRequest;
  selectedBid?: { id: string; name: string } | null;
  selectedLead: LeadDetails;
  onCreateBid?: (bidId: string) => void;
  disabled?: boolean;
  onChange?: (bidId: string | null) => void;
};

function ComboboxContentComponent(props: {
  combobox: ComboboxData;
  setSearchText: (text: string) => void;
  disabled?: boolean;
  loading: boolean;
  trigger?: ReactNode;
  footer?: ReactNode;
}) {
  const { trigger, combobox, setSearchText, disabled, loading, footer } = props;
  return (
    <ComboboxContent
      open={combobox.open}
      setOpen={(open: boolean) => {
        combobox.setOpen(open);
      }}
      trigger={trigger ? trigger : <div />}
    >
      <ComboboxInput
        placeholder={"Search bids..."}
        filterValue={combobox.filterValue}
        setFilterValue={(value) => {
          combobox.setFilterValue(value);
          setSearchText(value);
        }}
        open={combobox.open}
        setOpen={combobox.setOpen}
        value={[]}
        items={combobox.items}
        multiple={combobox.multiple}
        inputRef={combobox.inputRef}
        inputElementRef={combobox.inputElementRef}
        disabled={disabled}
      />
      <ComboboxEmpty
        filteredItems={combobox.filteredItems}
        filterValue={combobox.filterValue}
        disableAddingOptions
      >
        {loading ? <SkeletonText width="100%" /> : "No bids found."}
      </ComboboxEmpty>
      <ListScrollArea>
        <ComboboxList items={combobox.items} filteredItems={combobox.filteredItems}>
          <ComboboxCollection options={combobox.filteredItems as Option[]}>
            {(option: Option) => {
              return (
                <ComboboxItem
                  key={String(option.value)}
                  option={option}
                  value={combobox.value}
                  selectItem={combobox.selectItem}
                  multiple={combobox.multiple}
                  highlightedIndex={combobox.highlightedIndex}
                  filteredItems={combobox.filteredItems}
                >
                  <EntityOption label={option.label} />
                </ComboboxItem>
              );
            }}
          </ComboboxCollection>
        </ComboboxList>
      </ListScrollArea>
      {footer && <FooterContainer>{footer}</FooterContainer>}
    </ComboboxContent>
  );
}

export function RelatedBidCombobox<TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  defaultBidRequest,
  selectedBid,
  selectedLead,
  onCreateBid,
  disabled,
  onChange,
}: RelatedBidComboboxProps<TFieldValues>) {
  const { field } = useController({ control, name });
  const [searchText, setSearchText] = useState("");
  const [debouncedText] = useDebouncedValue(searchText, 300);
  const [clickedBidId, setClickedBidId] = useState<string | null>(null);

  const { data: bids, isLoading } = useBidsSimple(
    {
      ...defaultBidRequest,
      searchText: debouncedText || undefined,
      orderBy: BidSortBy.Name,
      order: BidSortOrder.Asc,
    },
    {
      page: 1,
      pageSize: 100,
    },
  );

  const getSelectedBid = (bid: { id: string; name: string }) => {
    if (selectedBid && selectedBid.id === bid.id) {
      return selectedBid;
    }
    if (!bid || !bids?.results) return null;
    return bids.results.find((b) => b.id === bid.id) || null;
  };

  const getOptions = (): Option[] => {
    const options: Option[] = bids?.results
      ? bids.results.map((bid) => ({
          label: bid.name,
          value: bid.id,
        }))
      : [];

    const unassignedOption: Option = {
      label: "Unassigned",
      value: "",
    };
    options.unshift(unassignedOption);

    return options;
  };

  const options = getOptions();
  const arrayValue = field.value === null ? [""] : field.value ? [field.value] : [];

  return (
    <Combobox
      items={options}
      value={arrayValue}
      onChange={(values) => {
        const newValue = values.length > 0 ? values[0] : null;
        field.onChange(newValue === "" ? null : newValue);
        if (onChange) {
          onChange(newValue === "" ? null : newValue !== null ? String(newValue) : null);
        }
      }}
      multiple={false}
    >
      {(combobox) => {
        const selectedValue = combobox.value[0];
        const currentBid =
          selectedValue && selectedValue !== ""
            ? getSelectedBid({ id: String(selectedValue), name: "" })
            : null;

        return (
          <PropertyInnerContainer>
            <Flex column gap={8} width="100%" minWidth={0}>
              {currentBid ? (
                <Fragment key={currentBid.id}>
                  <SelectedEntityCard
                    entity={currentBid}
                    dataType="bid"
                    combobox={combobox}
                    setEntityClickerId={setClickedBidId}
                    setIsOpen={null}
                    clickedEntityId={clickedBidId}
                    linkButtonProps={{ href: `${routes.bids}/${currentBid.id}` }}
                  />
                  <ComboboxContentComponent
                    combobox={combobox}
                    setSearchText={setSearchText}
                    disabled={disabled}
                    loading={isLoading}
                  />
                </Fragment>
              ) : (
                <ComboboxContentComponent
                  combobox={combobox}
                  setSearchText={setSearchText}
                  disabled={disabled}
                  loading={isLoading}
                  trigger={
                    <EmptyComboboxTrigger
                      label="Related Bid"
                      disabled={disabled}
                      onClick={() => {
                        combobox.setOpen(true);
                      }}
                    />
                  }
                  footer={
                    <CreateBidButton
                      defaultValues={{
                        name: selectedLead.name,
                        // A deactivated assignee can't take the bid, so it goes to its creator
                        assignedToId: selectedLead.assignedTo?.active
                          ? selectedLead.assignedTo.guid
                          : undefined,
                        leadId: selectedLead.id,
                        value: selectedLead.value ?? undefined,
                        entities: getLeadEntitiesForBid(selectedLead),
                      }}
                      createdFrom="lead"
                      trackingData={{ "Context source": "Bid combobox" }}
                      onCreateBid={onCreateBid}
                      buttonProps={{
                        buttonText: "Create Bid",
                        buttonType: "tertiary",
                        iconProps: { icon: "plus" },
                        fullWidth: true,
                      }}
                    />
                  }
                />
              )}
            </Flex>
          </PropertyInnerContainer>
        );
      }}
    </Combobox>
  );
}

const PropertyInnerContainer = styled.div({
  minWidth: 0,
  width: "100%",
});

const ListScrollArea = styled.div({
  maxHeight: 390,
  overflowY: "auto",
});

const FooterContainer = styled.div(({ theme }) => ({
  display: "flex",
  borderTop: `1px solid ${theme.colors.sysBorderDecorative}`,
  paddingTop: 4,
  marginTop: 4,
}));
